<template>
  <div class="dilution-solvent-ratio">
    <!-- 사내통용 원료명칭 검색 모달 -->
    <v-dialog v-model="searchModal" max-width="900px" max-height="700px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">사내통용 원료명칭 검색</span>
        </v-card-title>
        <v-card-text>
          <!-- 검색 영역 -->
          <v-form @submit.prevent="searchInternalMaterials">
            <div class="search-area mb-4">
              <v-row align="center">
                <v-col cols="3">
                  <span class="search-label">사내통용 원료</span>
                </v-col>
                <v-col cols="6">
                  <v-text-field v-model="searchKeyword" outlined dense placeholder="검색어를 입력하세요" @keyup.enter="searchInternalMaterials"></v-text-field>
                </v-col>
                <v-col cols="3">
                  <v-btn depressed color="green darken-1" class="white--text" @click="searchInternalMaterials"> 검색 </v-btn>
                </v-col>
              </v-row>
            </div>
          </v-form>

          <!-- 그리드 영역 -->
          <div class="grid-area" style="height: 350px; overflow: hidden">
            <RealGrid :domName="searchGridName" ref="searchGrid" :settings="searchGridSettings" @changePage="handleSearchPageChange" @click="handleSearchGridClick" @checkChange="handleSearchGridCheckChange" />
          </div>

          <!-- 검색 모달 페이징 -->
          <div class="pagination-container" v-if="searchPagination.totalRows > 0">
            <v-pagination :value="searchPagination.currentPage" :length="Math.ceil(searchPagination.totalRows / searchPagination.pageSize)" :total-visible="7" @input="handleSearchPageChange" circle></v-pagination>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeSearchModal">취소</v-btn>
          <v-btn color="green darken-1" text @click="selectMaterial" :disabled="!hasSelectedMaterial">확인</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <SetPopup ref="confirm" />
    <ValidationAlert ref="validationAlert" />

    <v-form ref="dilutionSolventRatio" lazy-validation>
      <div class="filter-box filter-box--row">
        <ul class="filter-list_free">
          <li>
            <h4>첨가제</h4>
            <v-select :items="additiveOptions" item-text="text" item-value="value" v-model="param.additive" outlined dense placeholder="전체" @change="onAdditiveChanged" />
          </li>
        </ul>
        <div class="btn-box">
          <v-card-actions>
            <v-btn depressed class="btn-reset" @click="reset">초기화</v-btn>
          </v-card-actions>
          <v-card-actions>
            <v-btn depressed class="btn-search" @click="search">검색</v-btn>
          </v-card-actions>
        </div>
      </div>
    </v-form>

    <h3 class="mt-2 mb-2 dp--row">
      <div style="margin-left: auto; display: flex; gap: 8px">
        <v-btn depressed class="btn-add" @click="addNewRow">추가</v-btn>
        <v-btn depressed class="btn-delete" @click="deleteSelectedRow">삭제</v-btn>
        <v-btn depressed class="btn-save" @click="saveData">저장</v-btn>
      </div>
    </h3>

    <RealGrid :domName="grid" ref="grid" :settings="settings" @changePage="search" @click="handleCellClick" />
  </div>
</template>

<script>
import { columns, fields, hideCheckBar } from '@/assets/grid/dilutionSolventRatio';
import { columns as searchColumns, fields as searchFields } from '@/assets/grid/internalMaterialNameSearch';
import RealGrid from '@/components/RealGrid.vue';
import SetPopup from '@/components/SetPopup.vue';
import ValidationAlert from '@/components/ValidationAlert.vue';
import { mapState, mapMutations } from 'vuex';
import { getDilutionSolventRatioList, insertDilutionSolventRatio, deleteDilutionSolventRatio, searchInternalMaterials, updatePriorities, getAdditives } from 'api/dilutionSolventRatio/dilutionSolventRatio';
import _ from 'lodash';

export default {
  data() {
    return {
      isFetching: false,
      isDataLoaded: false,
      grid: 'dilutionSolventRatio',
      settings: {
        columns: _.map(_.cloneDeep(columns), function (v) {
          // ID 컬럼은 편집 불가, 나머지는 편집 가능
          if (v.fieldName !== 'id' && v.fieldName !== 'regDt' && v.fieldName !== 'reg') {
            v.editable = true;
          }
          return v;
        }),
        fields,
        height: 666,
        errorMessage: '희석 용매비 데이터를 검색해주세요',
        fixCol: 1,
        hideCheckBar: hideCheckBar, // 체크박스 활성화
        radio: true, // 체크박스 단일 선택 (exclusive 모드)
        // 편집 관련 설정은 mounted에서 직접 설정
        displayOptions: {
          fitStyle: 'fill',
          rowResizable: true,
          columnResizable: true,
        },
      },
      param: {
        additive: '',
        pageSize: 100000,
        currentPage: 1,
      },
      additiveOptions: [],
      currentPage: 1,
      gridData: [],
      // 메인 그리드 페이징 관련 데이터
      pagination: {
        currentPage: 1,
        pageSize: 100000,
        totalRows: 0,
      },
      // 모달 관련 상태
      searchModal: false,
      selectedRowIndex: null,
      selectedColumnField: null, // 클릭된 컬럼 필드명 저장
      // 검색 모달 관련 데이터
      searchKeyword: '',
      selectedMaterial: null,
      selectedMaterialData: null, // 선택된 전체 데이터
      isItemSelected: false, // 선택 상태 추적
      searchGridName: 'internalMaterialSearch',
      searchGridSettings: {
        columns: searchColumns,
        fields: searchFields,
        height: 350,
        errorMessage: '검색된 사내통용 원료명칭이 없습니다',
        fixCol: 0, // fixCol 제거
        hideCheckBar: false, // 체크박스 표시
        radio: true, // 단일 선택
        fitStyle: 'fill', // 그리드 너비에 맞춰 컬럼 조정
        checkBar: {
          exclusive: true, // 단일 선택 강제
          showAll: false, // 전체 선택 체크박스 숨김
        },
      },
      searchGridData: [],
      // 검색 모달 페이징 관련 데이터
      searchPagination: {
        currentPage: 1,
        pageSize: 10,
        totalRows: 0,
      },
    };
  },
  computed: {
    ...mapState('common', ['code']),
    ...mapState('member', ['userInfo']),

    // 선택된 원료명칭이 있는지 확인
    hasSelectedMaterial() {
      return this.isItemSelected && (this.selectedMaterial !== null || this.selectedMaterialData !== null);
    },
  },
  components: {
    RealGrid,
    SetPopup,
    ValidationAlert,
  },
  mounted() {
    // 글로벌 함수 등록 (HTML 렌더링된 셀에서 호출용)
    window.openSearchModal = (fieldName, rowIndex) => {
      // 해당 행의 데이터 확인
      const allData = this.$refs.grid.getJsonRows();
      const rowData = allData[rowIndex];

      // ID가 있는 행은 모달 열기 금지
      if (rowData && rowData.id != null && rowData.id !== '') {
        this.openPopup('기존 데이터는 수정할 수 없습니다.');
        return;
      }

      this.selectedRowIndex = rowIndex;
      this.selectedColumnField = fieldName;
      this.searchModal = true;

      // 선택 상태 초기화
      this.isItemSelected = false;
      this.selectedMaterial = null;
      this.selectedMaterialData = null;

      // 모달이 열릴 때 초기 검색 실행
      this.$nextTick(() => {
        this.searchInternalMaterials();
      });
    };

    this.loadData();

    // 첨가제 드롭다운 옵션 로드
    this.fetchAdditives();
  },

  beforeDestroy() {
    // 컴포넌트 파괴 시 글로벌 함수 정리
    if (window.openSearchModal) {
      delete window.openSearchModal;
    }
  },
  methods: {
    ...mapMutations('modal', ['SET_MODAL']),
    ...mapMutations('popup', ['SET_POPUP']),

    loadData(v) {
      this.search(v);
    },

    async fetchAdditives() {
      try {
        const res = await getAdditives();
        const service = res.data;
        const options = service?.data?.data?.options || service?.data?.options || [];
        // '전체' 옵션을 첫 번째로 추가하고 문자열 배열을 {text,value}로 변환
        const mapped = [{ text: '전체', value: '' }].concat((Array.isArray(options) ? options : []).map(v => ({ text: v, value: v })));
        this.additiveOptions = mapped;
        // 기본 선택은 전체
        if (this.param.additive === undefined || this.param.additive === null) {
          this.param.additive = '';
        }
      } catch (e) {
        // 무시하고 빈 옵션 유지
      }
    },

    onAdditiveChanged() {
      // 페이지 초기화 후 검색
      this.param.currentPage = 1;
      this.search();
    },

    async search(v) {
      if (this.isFetching) return;
      this.isFetching = true;

      try {
        if (_.isNumber(v)) {
          this.currentPage = v;
          this.param.currentPage = v;
        }

        const res = await getDilutionSolventRatioList({ ...this.param });
        const response = res.data;
        const serviceResult = response.data;

        let items = [];
        let page = {};

        if (serviceResult && serviceResult.success) {
          items = serviceResult.data.items || [];
          page = serviceResult.data.params || {};
        } else {
          items = serviceResult.items || serviceResult.data?.items || [];
          page = serviceResult.params || serviceResult.data?.params || {};
        }

        // 페이징 정보 업데이트
        this.pagination.currentPage = page.currentPage || 1;
        this.pagination.totalRows = page.totalRows || 0;

        this.isDataLoaded = true;
        this.gridData = items;
        this.$refs.grid.loadData(items);
        this.$refs.grid.setPage(page);

        // 그리드 데이터 로드 후 편집 제한 설정
        this.$nextTick(() => {
          this.setupGridEditRestrictions();
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('희석 용매비 목록 조회 중 오류 발생:', err);
        this.openPopup('데이터 조회에 실패했습니다.');
      } finally {
        this.isFetching = false;
      }
    },

    // 그리드 편집 제한 설정
    setupGridEditRestrictions() {
      if (this.$refs.grid && this.$refs.grid.gv) {
        const enableDrag = !!(this.param && typeof this.param.additive === 'string' && this.param.additive.trim() !== '');
        this.$refs.grid.gv.editOptions.movable = enableDrag;
        this.$refs.grid.gv.rowIndicator.draggableSelectedRows = enableDrag;
        const vm = this;
        this.$refs.grid.gv.dataDropOptions.callback = function (source, sourceItems, target, targetItem) {
          console.log('row from ' + sourceItems + ' to ' + targetItem);
          try {
            // 동일 그리드 내 드래그앤드롭만 처리
            if (source !== target) return;

            const dp = vm.$refs.grid && vm.$refs.grid.dp ? vm.$refs.grid.dp : null;
            if (!dp) return;

            // RealGrid v2: source.getRowsOfItems(items)로 실제 행 인덱스 배열을 추출
            const rows = source && typeof source.getRowsOfItems === 'function' ? source.getRowsOfItems(sourceItems) : [];
            if (!rows || rows.length === 0) return;

            // 연속 구간 이동(첫 행 인덱스, 개수, 목표 인덱스)
            var first = rows[0];
            var count = rows.length;

            // targetItem은 드롭 목표 인덱스
            var targetIndex = typeof targetItem === 'number' ? targetItem : dp.getRowCount();

            // 드래그 구간과 목표 인덱스 관계에 따른 보정
            if (targetIndex > first) {
              if (targetIndex <= first + count) {
                // 바로 아래 등 자기 구간으로 드롭한 경우: 블록 바로 뒤로 이동
                targetIndex = first + count;
              } else {
                // 자기 구간 이후로 내리는 경우: 제거 후 삽입 인덱스 보정
                targetIndex = targetIndex - count;
              }
            }

            dp.moveRows(first, count, targetIndex);
            vm.$refs.grid.gv.refresh();

            // 드래그 후 우선순위 재계산: index+1 -> priority
            try {
              const updatedItems = [];
              for (let i = 0; i < dp.getRowCount(); i++) {
                const rowJson = typeof dp.getJsonRow === 'function' ? dp.getJsonRow(i) : vm.$refs.grid && vm.$refs.grid.gv && typeof vm.$refs.grid.gv.getValues === 'function' ? vm.$refs.grid.gv.getValues(i) : null;
                if (!rowJson) continue;
                const newPriority = i + 1;
                // 그리드 표시도 갱신
                if (typeof vm.$refs.grid.gv.setValue === 'function') {
                  vm.$refs.grid.gv.setValue(i, 'priority', newPriority);
                } else if (typeof dp.setValue === 'function') {
                  dp.setValue(i, 'priority', newPriority);
                }
                if (rowJson.id != null && rowJson.id !== '') {
                  updatedItems.push({ id: rowJson.id, priority: newPriority });
                }
              }

              // 백엔드에 일괄 업데이트 호출
              if (updatedItems.length > 0) {
                updatePriorities(updatedItems)
                  .then(() => {
                    // 성공 시 재조회로 일관성 유지
                    vm.search();
                  })
                  .catch(err => {
                    console.error('우선순위 업데이트 실패:', err);
                    vm.openPopup('우선순위 저장에 실패했습니다.');
                  });
              }
            } catch (logErr) {
              console.error('Failed to recalc priority and update:', logErr);
            }
          } catch (e) {
            console.error('drop callback error:', e);
          }
        };
        // 동적으로 행별 편집 제한 설정
        this.$refs.grid.gv.setRowStyleCallback(function (grid, item, fixed) {
          var ret = {};
          var id = grid.getValue(item.index, 'id');

          // ID 값이 있으면 편집 불가
          if (id != null && id !== '') {
            ret.editable = false;
          }

          return ret;
        });
      }
    },

    // 메인 그리드 페이지 변경 핸들러
    handleMainPageChange(page) {
      this.pagination.currentPage = page;
      this.param.currentPage = page;
      this.search();
    },

    reset() {
      this.param = {
        additive: '',
        pageSize: 100000,
        currentPage: 1,
      };
      this.currentPage = 1;
      // 페이징 정보 초기화
      this.pagination.currentPage = 1;
      this.pagination.totalRows = 0;
      this.search();
    },

    // 새 로우 추가
    addNewRow() {
      const dp = this.$refs.grid && this.$refs.grid.dp ? this.$refs.grid.dp : null;
      if (dp && typeof dp.getRowCount === 'function' && typeof dp.getRowState === 'function') {
        const rowCount = dp.getRowCount();
        for (let i = 0; i < rowCount; i++) {
          const state = dp.getRowState(i);
          if (state === 'created') {
            this.openPopup('이미 추가 중인 행이 있습니다. 저장 후 다시 추가해주세요.');
            return;
          }
        }
      }

      this.$refs.grid.gv.editOptions.movable = false;
      this.$refs.grid.gv.rowIndicator.draggableSelectedRows = false;
      const newRow = {
        id: null, // 새 데이터는 ID가 null
        additive: '',
        dilutionMaterial1: '',
        dilutionMaterial2: '',
        additiveRatio: null,
        dilutionRatio1: null,
        dilutionRatio2: null,
        priority: null,
        remark: '',
        regDt: new Date().toISOString().slice(0, 10),
        reg: this.userInfo?.memberId || '',
      };

      this.$refs.grid.addRow(newRow);
    },

    // 선택된 로우 삭제
    async deleteSelectedRow() {
      const selectedRows = this.$refs.grid.gv.getCheckedRows(true, false, false);

      if (selectedRows.length === 0) {
        this.openPopup('삭제할 항목을 선택해주세요.');
        return;
      }

      // 확인 팝업
      this.openPopup('선택한 항목을 삭제하시겠습니까?', true, async () => {
        try {
          const selectedRow = this.$refs.grid.dp.getJsonRow(selectedRows[0], false);
          // 백엔드에 삭제 요청 (ID가 있는 기존 데이터인 경우만)
          if (selectedRow.id != null && selectedRow.id !== '') {
            await deleteDilutionSolventRatio(selectedRow.id);
          }

          // 그리드 데이터에서 삭제
          this.gridData.splice(selectedRows[0], 1);

          // 그리드 새로고침
          this.$refs.grid.loadData(this.gridData);
          this.openPopup('삭제되었습니다.');
          this.search();
        } catch (err) {
          console.error('삭제 중 오류 발생:', err);
          this.openPopup('삭제에 실패했습니다.');
        }
      });
    },

    // 그리드 데이터 저장 (선택된 행만 처리)
    async saveData() {
      try {
        // 선택된 행만 가져오기
        const selectedRows = this.$refs.grid.gv.getCheckedRows(true, false, false);

        if (selectedRows.length === 0) {
          this.openPopup('저장할 항목을 선택해주세요.');
          return;
        }

        const allData = this.$refs.grid.getJsonRows();
        const validationErrors = [];
        let hasNewDataToSave = false;

        // 선택된 행만 처리
        for (const rowIndex of selectedRows) {
          const row = allData[rowIndex];

          // 기존 데이터(ID가 있는)는 수정 불가
          if (row.id != null && row.id !== '') {
            continue; // 기존 데이터는 건너뛰기
          }

          hasNewDataToSave = true;

          // 필수값 검사 (새로 추가된 데이터만)
          if (!row.additive || row.additive.trim() === '' || row.additive.trim() === '선택없음') {
            validationErrors.push(`첨가제는 필수이며 "선택없음"은 허용되지 않습니다.`);
          }
          if (!row.dilutionMaterial1 || row.dilutionMaterial1.trim() === '') {
            validationErrors.push(`희석원료1은 필수입니다.`);
          }
          // 희석원료2에 "선택없음" 값이 올 수 없도록 검증
          if (row.dilutionMaterial2 && row.dilutionMaterial2.trim() === '선택없음') {
            validationErrors.push(`희석원료2에는 "선택없음"을 입력할 수 없습니다.`);
          }
          if (!row.additiveRatio || row.additiveRatio === null || row.additiveRatio === '') {
            validationErrors.push(`첨가제 비율은 필수입니다.`);
          }
          if (!row.dilutionRatio1 || row.dilutionRatio1 === null || row.dilutionRatio1 === '') {
            validationErrors.push(`희석 원료비율1은 필수입니다.`);
          }

          // 명칭과 비율 세트 검사 (명칭이 있으면 비율도 있어야 하고, 비율이 있으면 명칭도 있어야 함)

          // 첨가제와 첨가제 비율 세트 검사
          const hasAdditive = row.additive && row.additive.trim() !== '';
          const hasAdditiveRatio = row.additiveRatio !== null && row.additiveRatio !== '' && row.additiveRatio !== undefined;

          if (hasAdditive && !hasAdditiveRatio) {
            validationErrors.push(`첨가제가 입력되었으면 첨가제 비율도 입력해야 합니다.`);
          }
          if (hasAdditiveRatio && !hasAdditive) {
            validationErrors.push(`첨가제 비율이 입력되었으면 첨가제도 입력해야 합니다.`);
          }

          // 희석원료1과 희석 원료비율1 세트 검사
          const hasDilutionMaterial1 = row.dilutionMaterial1 && row.dilutionMaterial1.trim() !== '';
          const hasDilutionRatio1 = row.dilutionRatio1 !== null && row.dilutionRatio1 !== '' && row.dilutionRatio1 !== undefined;
          const isDilutionMaterial1Default = row.dilutionMaterial1 && row.dilutionMaterial1.trim() === '선택없음';

          if (hasDilutionMaterial1 && !hasDilutionRatio1) {
            validationErrors.push(`희석원료1이 입력되었으면 희석 원료비율1도 입력해야 합니다.`);
          }
          if (hasDilutionRatio1 && !hasDilutionMaterial1) {
            validationErrors.push(`희석 원료비율1이 입력되었으면 희석원료1도 입력해야 합니다.`);
          }

          // 희석원료2와 희석 원료비율2 세트 검사 (선택사항이므로 둘 다 입력되지 않아도 됨)
          const hasDilutionMaterial2 = row.dilutionMaterial2 && row.dilutionMaterial2.trim() !== '' && row.dilutionMaterial2.trim() !== '선택없음';
          const hasDilutionRatio2 = row.dilutionRatio2 !== null && row.dilutionRatio2 !== '' && row.dilutionRatio2 !== undefined;

          if (hasDilutionMaterial2 && !hasDilutionRatio2) {
            validationErrors.push(`희석원료2가 입력되었으면 희석 원료비율2도 입력해야 합니다.`);
          }
          if (hasDilutionRatio2 && !hasDilutionMaterial2) {
            validationErrors.push(`희석 원료비율2가 입력되었으면 희석원료2도 입력해야 합니다.`);
          }

          // 비율 값들이 정수인지 검사
          if (row.additiveRatio !== null && row.additiveRatio !== '' && !Number.isInteger(Number(row.additiveRatio))) {
            validationErrors.push(`첨가제 비율은 정수만 입력 가능합니다.`);
          }
          if (row.dilutionRatio1 !== null && row.dilutionRatio1 !== '' && !Number.isInteger(Number(row.dilutionRatio1))) {
            validationErrors.push(`희석 원료비율1은 정수만 입력 가능합니다.`);
          }
          if (row.dilutionRatio2 !== null && row.dilutionRatio2 !== '' && !Number.isInteger(Number(row.dilutionRatio2))) {
            validationErrors.push(`희석 원료비율2는 정수만 입력 가능합니다.`);
          }

          // 비율 값이 양수인지 검사
          if (row.additiveRatio !== null && row.additiveRatio !== '' && Number(row.additiveRatio) <= 0) {
            validationErrors.push(`첨가제 비율은 0보다 큰 값이어야 합니다.`);
          }
          // 희석원료1이 "선택없음"이 아닌 경우에만 양수 검사 (0은 허용하지 않음)
          if (row.dilutionRatio1 !== null && row.dilutionRatio1 !== '' && !isDilutionMaterial1Default && Number(row.dilutionRatio1) <= 0) {
            validationErrors.push(`희석 원료비율1은 0보다 큰 값이어야 합니다.`);
          }
          // 희석원료1이 "선택없음"인 경우 0도 허용
          if (row.dilutionRatio1 !== null && row.dilutionRatio1 !== '' && isDilutionMaterial1Default && Number(row.dilutionRatio1) < 0) {
            validationErrors.push(`희석 원료비율1은 0 이상의 값이어야 합니다.`);
          }
          if (row.dilutionRatio2 !== null && row.dilutionRatio2 !== '' && Number(row.dilutionRatio2) <= 0) {
            validationErrors.push(`희석 원료비율2는 0보다 큰 값이어야 합니다.`);
          }
        }

        // 저장할 새로운 데이터가 없는 경우
        if (!hasNewDataToSave) {
          this.openPopup('새로 추가된 데이터만 저장 가능합니다. 기존 데이터는 수정할 수 없습니다.');
          return;
        }

        if (validationErrors.length > 0) {
          this.showValidationError(validationErrors);
          return;
        }

        // API 호출 처리 (새로 추가된 데이터만)
        for (const rowIndex of selectedRows) {
          const row = allData[rowIndex];

          // 새 데이터만 등록 (ID가 null이거나 빈 문자열인 경우)
          if (row.id == null || row.id === '') {
            const insertData = {
              additive: row.additive,
              dilutionMaterial1: row.dilutionMaterial1,
              dilutionMaterial2: row.dilutionMaterial2,
              additiveRatio: row.additiveRatio,
              dilutionRatio1: row.dilutionRatio1,
              dilutionRatio2: row.dilutionRatio2,
              priority: row.priority,
              remark: row.remark,
            };

            await insertDilutionSolventRatio(insertData);
          }
        }

        // 저장 후 목록 새로고침
        this.search();
        this.openPopup('저장되었습니다.');
      } catch (err) {
        console.error('저장 중 오류 발생:', err);

        let errorMessage = '저장에 실패했습니다.';
        if (err.response && err.response.data) {
          if (err.response.data.data) {
            // data 필드에 구체적인 메시지가 있는 경우 (예: "이미 존재하는 첨가제와 희석원료1 조합입니다.")
            errorMessage = err.response.data.data;
          } else if (err.response.data.errorMessage) {
            // errorMessage 필드 사용
            errorMessage = err.response.data.errorMessage;
          }
        }

        this.openPopup(errorMessage);
      }
    },

    showValidationError(errors) {
      // 커스텀 ValidationAlert 컴포넌트 사용
      this.$refs.validationAlert.show(errors);
    },

    openPopup(text, closable = false, cb) {
      this.SET_POPUP({
        title: '알림',
        width: 300,
        closable: closable,
        text: text,
      });
      this.$refs.confirm.openPopup(cb);
    },

    // 셀 클릭 이벤트 처리
    handleCellClick(event) {
      const { grid, clickData } = event;
      const column = clickData.column;
      const itemIndex = clickData.dataRow || clickData.itemIndex;

      // 첨가제, 희석원료1, 희석원료2 컬럼 클릭 시 모달 열기 (name 또는 fieldName으로 체크)
      if (column && (column === 'additive' || column === 'dilutionMaterial1' || column === 'dilutionMaterial2')) {
        // 해당 행의 데이터 확인
        const allData = this.$refs.grid.getJsonRows();
        const rowData = allData[itemIndex];

        // ID가 있는 행은 모달 열기 금지
        if (rowData && rowData.id != null && rowData.id !== '') {
          this.openPopup('기존 데이터는 수정할 수 없습니다.');
          return;
        }

        this.selectedRowIndex = itemIndex;
        this.selectedColumnField = column;
        this.searchModal = true;

        // 선택 상태 초기화
        this.isItemSelected = false;
        this.selectedMaterial = null;
        this.selectedMaterialData = null;

        // 모달이 열릴 때 초기 검색 실행
        this.$nextTick(() => {
          this.searchInternalMaterials();
        });
      }
    },

    // 검색 모달 닫기
    closeSearchModal() {
      this.searchModal = false;
      this.selectedRowIndex = null;
      this.selectedColumnField = null;
      this.searchKeyword = '';
      this.selectedMaterial = null;
      this.selectedMaterialData = null;
      this.isItemSelected = false;
      this.searchGridData = [];
      // 검색 페이징 정보 초기화
      this.searchPagination.currentPage = 1;
      this.searchPagination.totalRows = 0;
    },

    // 사내통용 원료명칭 검색
    async searchInternalMaterials(page = 1) {
      try {
        // page가 객체인 경우 숫자로 변환
        const pageNumber = typeof page === 'object' ? 1 : page;

        const params = {
          searchKeyword: this.searchKeyword,
          currentPage: pageNumber,
          pageSize: this.searchPagination.pageSize,
        };

        const response = await searchInternalMaterials(params);
        const result = response.data.data;

        // 검색 페이징 정보 업데이트
        this.searchPagination.currentPage = pageNumber;
        this.searchPagination.totalRows = (result.params && result.params.totalRows) || 0;

        this.searchGridData = result.items || [];
        this.$refs.searchGrid.loadData(this.searchGridData);
        this.$refs.searchGrid.setPage(result.params || {});

        // 검색 후 선택 상태 초기화
        this.isItemSelected = false;
        this.selectedMaterial = null;
        this.selectedMaterialData = null;
      } catch (error) {
        console.error('사내통용 원료명칭 검색 중 오류:', error);
        this.openPopup('검색 중 오류가 발생했습니다.');
      }
    },

    // 검색 그리드 페이지 변경 처리
    handleSearchPageChange(page) {
      // page가 이벤트 객체인 경우 처리
      const pageNumber = typeof page === 'object' ? page.currentPage || 1 : page;
      this.searchInternalMaterials(pageNumber);
    },

    // 검색 그리드 클릭 이벤트 처리
    handleSearchGridClick(event) {
      const { grid, clickData } = event;
      const itemIndex = clickData.dataRow || clickData.itemIndex;

      if (itemIndex >= 0) {
        const selectedData = grid.getValue(itemIndex, 'internalMaterialName');
        this.selectedMaterial = selectedData;

        // 클릭 시에도 선택 상태 업데이트
        setTimeout(() => {
          this.updateSelectionState();
        }, 100);
      }
    },

    // 검색 그리드 체크박스 변경 이벤트 처리
    handleSearchGridCheckChange(event) {
      this.updateSelectionState();
    },

    // 선택 상태 업데이트
    updateSelectionState() {
      this.$nextTick(() => {
        try {
          if (this.$refs.searchGrid) {
            const checkedRows = this.$refs.searchGrid.getCheckedRow();

            if (checkedRows && checkedRows.length > 0) {
              this.isItemSelected = true;
              this.selectedMaterialData = checkedRows[0].data;
              this.selectedMaterial = this.selectedMaterialData.internalMaterialName;
            } else {
              this.isItemSelected = false;
              this.selectedMaterialData = null;
              this.selectedMaterial = null;
            }
          }
        } catch (error) {
          this.isItemSelected = false;
        }
      });
    },

    // 원료명칭 선택 확인
    selectMaterial() {
      if (this.isItemSelected && this.selectedMaterial && this.selectedRowIndex !== null && this.selectedColumnField !== null) {
        // 선택된 원료명칭을 원래 그리드의 해당 행에 설정
        const allData = this.$refs.grid.getJsonRows();
        if (allData[this.selectedRowIndex]) {
          allData[this.selectedRowIndex][this.selectedColumnField] = this.selectedMaterial;
          this.$refs.grid.loadData(allData);
          this.gridData = allData;
        }

        this.closeSearchModal();
      } else {
        this.openPopup('원료명칭을 선택해주세요.');
      }
    },
  },
};
</script>

<style lang="scss">
.dilution-solvent-ratio {
  .v-input__slot {
    width: 100% !important;
  }

  // v-select의 clear 아이콘(X) 숨기기
  ::v-deep .v-input__icon--clear {
    display: none !important;
  }

  li {
    display: block !important;
    &:first-child {
      width: 200px !important;
      min-width: 200px !important;
      max-width: 200px !important;
    }

    &:nth-child(2) {
      width: 250px !important;
      min-width: 250px !important;
      max-width: 250px !important;
    }
    h4 {
      margin-bottom: 8px !important;
      font-size: 14px !important;
      font-weight: 600 !important;
      color: #333 !important;
    }
  }
  // 검색 모달 스타일
  .search-area {
    .search-label {
      font-weight: 500;
      color: #333;
      line-height: 40px; // 입력창과 높이 맞춤
    }
  }

  .grid-area {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 0;

    // 그리드 내부 스타일 조정
    ::v-deep .real {
      width: 100% !important;

      .rg-container {
        width: 100% !important;
      }

      .rg-check-bar {
        width: 50px !important; // 체크박스 컬럼 너비 조정
      }
    }
  }

  // 페이징 컨테이너 스타일
  .pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e0e0e0;
  }
}
</style>
